/* eslint-disable @typescript-eslint/consistent-type-definitions -- Zero mutator/query arg types must be `type` aliases to satisfy the ReadonlyJSONValue constraint; interfaces lack an implicit index signature. */
import { defineMutator } from "@rocicorp/zero"

import type {
	NotificationScope,
	NotificationSettings,
	PushSchedule,
	ZeroJwtPayload,
} from "@productlane/lib/src/browser"
import {
	applyPushPreferenceArgs,
	COMPLETED_ENTITY_STATUSES,
	COMPLETED_THREAD_STATES,
	parseNotificationSettings,
	patchNotificationSettings,
	setDesktopNotifications,
} from "@productlane/lib/src/browser"

import type { MutatorTx, NotificationRow } from "../schema"
import type { ReadonlyJSONValue } from "@rocicorp/zero"

export type SetNotificationsReadArgs = {
	ids: string[]
	read: boolean
	at: number
}
export type SetNotificationsArchivedArgs = {
	ids: string[]
	archived: boolean
	at: number
}
export type DeleteNotificationsArgs = { ids: string[] }
export type MarkThreadNotificationsReadArgs = { threadId: string; at: number }
export type NotificationBulkArgs = { at: number }

export type SetNotificationSubscriptionArgs = {
	/** Used only when no row exists yet; client-generated so retries stay idempotent. */
	id: string
	entityType: string
	entityId: string
	isSubscribed: boolean
	/** A teammate to subscribe or remove; omitted means the caller. */
	userId?: string
}

export type NotificationPreferencePatch = {
	inbox?: NotificationScope
	desktop?: boolean
	push?: boolean
	/** ThreadFilters; null clears the type's default filter. */
	filter?: Readonly<Record<string, ReadonlyJSONValue>> | null
}

export type PatchNotificationPreferenceArgs = {
	id: string
	type: string
	patch: NotificationPreferencePatch
}

export type SetDesktopNotificationsArgs = {
	/** Used only when the member has no preference row yet. */
	id: string
	enabled: boolean
}

export type SetPushNotificationsArgs = {
	/** Used only when the member has no preference row yet. */
	id: string
	enabled?: boolean
	schedule?: PushSchedule
}

async function ownRows(tx: MutatorTx, ids: string[], auth: ZeroJwtPayload) {
	if (ids.length === 0) return []
	const rows = await tx.query.notifications.where("id", "IN", ids).run()
	return rows.filter(
		(r) => r.userId === auth.userId && r.workspaceId === auth.workspaceId,
	)
}

/** Ids of the threads, issues and projects behind these rows that are done, canceled or deleted. */
async function completedEntityIds(
	tx: MutatorTx,
	auth: ZeroJwtPayload,
	rows: readonly NotificationRow[],
) {
	const idsOf = (entityType: string) => [
		...new Set(
			rows.flatMap((r) =>
				r.threadId ? [] : r.entityType === entityType ? [r.entityId] : [],
			),
		),
	]
	const threadIds = [
		...new Set(rows.flatMap((r) => (r.threadId ? [r.threadId] : []))),
	]
	const issueIds = idsOf("issue")
	const projectIds = idsOf("project")
	const [threads, issues, projects] = await Promise.all([
		threadIds.length === 0
			? []
			: tx.query.feedbacks
					.where("workspaceId", auth.workspaceId)
					.where("id", "IN", threadIds)
					.run(),
		issueIds.length === 0
			? []
			: tx.query.issues
					.where("workspaceId", auth.workspaceId)
					.where("id", "IN", issueIds)
					.run(),
		projectIds.length === 0
			? []
			: tx.query.projects
					.where("workspaceId", auth.workspaceId)
					.where("id", "IN", projectIds)
					.run(),
	])
	const threadStates: readonly string[] = COMPLETED_THREAD_STATES
	return new Set([
		...threads
			.filter((t) => t.isDeleted || threadStates.includes(t.state))
			.map((t) => t.id),
		...issues
			.filter(
				(i) => i.isDeleted || COMPLETED_ENTITY_STATUSES.includes(i.status),
			)
			.map((i) => i.id),
		...projects
			.filter(
				(p) => p.isDeleted || COMPLETED_ENTITY_STATUSES.includes(p.state ?? ""),
			)
			.map((p) => p.id),
	])
}

export const notificationMutators = {
	setRead: defineMutator(
		async ({
			tx,
			args,
			ctx: auth,
		}: {
			tx: MutatorTx
			args: SetNotificationsReadArgs
			ctx: ZeroJwtPayload
		}) => {
			for (const row of await ownRows(tx, args.ids, auth)) {
				await tx.mutate.notifications.update({
					id: row.id,
					readAt: args.read ? (row.readAt ?? args.at) : null,
				})
			}
		},
	),
	setArchived: defineMutator(
		async ({
			tx,
			args,
			ctx: auth,
		}: {
			tx: MutatorTx
			args: SetNotificationsArchivedArgs
			ctx: ZeroJwtPayload
		}) => {
			for (const row of await ownRows(tx, args.ids, auth)) {
				await tx.mutate.notifications.update({
					id: row.id,
					archivedAt: args.archived ? (row.archivedAt ?? args.at) : null,
					readAt: args.archived ? (row.readAt ?? args.at) : row.readAt,
				})
			}
		},
	),
	remove: defineMutator(
		async ({
			tx,
			args,
			ctx: auth,
		}: {
			tx: MutatorTx
			args: DeleteNotificationsArgs
			ctx: ZeroJwtPayload
		}) => {
			for (const row of await ownRows(tx, args.ids, auth)) {
				await tx.mutate.notifications.delete({ id: row.id })
			}
		},
	),
	removeAll: defineMutator(
		async ({
			tx,
			ctx: auth,
		}: {
			tx: MutatorTx
			args: NotificationBulkArgs
			ctx: ZeroJwtPayload
		}) => {
			const rows = await tx.query.notifications
				.where("userId", auth.userId)
				.where("workspaceId", auth.workspaceId)
				.run()
			for (const row of rows) {
				await tx.mutate.notifications.delete({ id: row.id })
			}
		},
	),
	// Optimistic over whatever threads, issues and projects are synced; the server override sees them all.
	removeCompleted: defineMutator(
		async ({
			tx,
			ctx: auth,
		}: {
			tx: MutatorTx
			args: NotificationBulkArgs
			ctx: ZeroJwtPayload
		}) => {
			const rows = await tx.query.notifications
				.where("userId", auth.userId)
				.where("workspaceId", auth.workspaceId)
				.run()
			const completed = await completedEntityIds(tx, auth, rows)
			for (const row of rows) {
				const id = row.threadId ?? row.entityId
				if (completed.has(id)) {
					await tx.mutate.notifications.delete({ id: row.id })
				}
			}
		},
	),
	removeAllRead: defineMutator(
		async ({
			tx,
			ctx: auth,
		}: {
			tx: MutatorTx
			args: NotificationBulkArgs
			ctx: ZeroJwtPayload
		}) => {
			const rows = await tx.query.notifications
				.where("userId", auth.userId)
				.where("workspaceId", auth.workspaceId)
				.where("readAt", "IS NOT", null)
				.run()
			for (const row of rows) {
				await tx.mutate.notifications.delete({ id: row.id })
			}
		},
	),
	// The client only holds the synced window; the server override updates every row.
	markAllRead: defineMutator(
		async ({
			tx,
			args,
			ctx: auth,
		}: {
			tx: MutatorTx
			args: NotificationBulkArgs
			ctx: ZeroJwtPayload
		}) => {
			const rows = await tx.query.notifications
				.where("userId", auth.userId)
				.where("workspaceId", auth.workspaceId)
				.where("readAt", "IS", null)
				.run()
			for (const row of rows) {
				await tx.mutate.notifications.update({ id: row.id, readAt: args.at })
			}
		},
	),
	archiveAllRead: defineMutator(
		async ({
			tx,
			args,
			ctx: auth,
		}: {
			tx: MutatorTx
			args: NotificationBulkArgs
			ctx: ZeroJwtPayload
		}) => {
			const rows = await tx.query.notifications
				.where("userId", auth.userId)
				.where("workspaceId", auth.workspaceId)
				.where("readAt", "IS NOT", null)
				.where("archivedAt", "IS", null)
				.run()
			for (const row of rows) {
				await tx.mutate.notifications.update({
					id: row.id,
					archivedAt: args.at,
				})
			}
		},
	),
	markThreadRead: defineMutator(
		async ({
			tx,
			args,
			ctx: auth,
		}: {
			tx: MutatorTx
			args: MarkThreadNotificationsReadArgs
			ctx: ZeroJwtPayload
		}) => {
			const rows = await tx.query.notifications
				.where("userId", auth.userId)
				.where("workspaceId", auth.workspaceId)
				.where("threadId", args.threadId)
				.where("readAt", "IS", null)
				.run()
			for (const row of rows) {
				await tx.mutate.notifications.update({ id: row.id, readAt: args.at })
			}
		},
	),
}

export const notificationSubscriptionMutators = {
	set: defineMutator(
		async ({
			tx,
			args,
			ctx: auth,
		}: {
			tx: MutatorTx
			args: SetNotificationSubscriptionArgs
			ctx: ZeroJwtPayload
		}) => {
			const userId = args.userId ?? auth.userId
			const source = userId === auth.userId ? "manual" : "added"
			const existing = await tx.query.notificationSubscriptions
				.where("userId", userId)
				.where("workspaceId", auth.workspaceId)
				.where("entityType", args.entityType)
				.where("entityId", args.entityId)
				.one()
				.run()
			if (existing) {
				await tx.mutate.notificationSubscriptions.update({
					id: existing.id,
					isSubscribed: args.isSubscribed,
					source,
				})
				return
			}
			await tx.mutate.notificationSubscriptions.insert({
				id: args.id,
				entityType: args.entityType,
				entityId: args.entityId,
				isSubscribed: args.isSubscribed,
				source,
				workspaceId: auth.workspaceId,
				userId,
			})
		},
	),
}

async function updateOwnSettings(
	tx: MutatorTx,
	auth: ZeroJwtPayload,
	id: string,
	update: (settings: NotificationSettings) => NotificationSettings,
) {
	const existing = await tx.query.notificationPreferences
		.where("workspaceId", auth.workspaceId)
		.where("userId", auth.userId)
		.one()
		.run()
	const settings = update(
		parseNotificationSettings(existing?.settings),
	) as unknown as ReadonlyJSONValue
	if (existing) {
		await tx.mutate.notificationPreferences.update({
			id: existing.id,
			settings,
		})
		return
	}
	await tx.mutate.notificationPreferences.insert({
		id,
		settings,
		workspaceId: auth.workspaceId,
		userId: auth.userId,
	})
}

export const notificationPreferenceMutators = {
	setDesktop: defineMutator(
		async ({
			tx,
			args,
			ctx: auth,
		}: {
			tx: MutatorTx
			args: SetDesktopNotificationsArgs
			ctx: ZeroJwtPayload
		}) => {
			await updateOwnSettings(tx, auth, args.id, (s) =>
				setDesktopNotifications(s, args.enabled),
			)
		},
	),
	setPush: defineMutator(
		async ({
			tx,
			args,
			ctx: auth,
		}: {
			tx: MutatorTx
			args: SetPushNotificationsArgs
			ctx: ZeroJwtPayload
		}) => {
			await updateOwnSettings(tx, auth, args.id, (s) =>
				applyPushPreferenceArgs(s, args),
			)
		},
	),
	patch: defineMutator(
		async ({
			tx,
			args,
			ctx: auth,
		}: {
			tx: MutatorTx
			args: PatchNotificationPreferenceArgs
			ctx: ZeroJwtPayload
		}) => {
			await updateOwnSettings(tx, auth, args.id, (s) =>
				patchNotificationSettings(s, args.type, args.patch),
			)
		},
	),
}
